import { notification } from 'antd'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  createGoldenPromptAdmin,
  deleteGoldenPromptAdmin,
  enqueueMutationAdmin,
  getGoldenPromptAdmin,
  getMutationStatusAdmin,
  listGoldenPromptsAdmin,
  updateGoldenPromptAdmin,
  uploadGoldenPromptsAdmin,
} from './goldenPromptsAdminApi'
import {
  GoldenPromptCreateInput,
  GoldenPromptFilters,
  GoldenPromptUpdateInput,
  MutationStatusResponse,
} from './goldenPromptsAdminModel'

const ADMIN_LIST_KEY = ['goldenPromptsAdmin', 'list'] as const
const MUTATION_STATUS_KEY = ['goldenPromptsAdmin', 'mutationStatus'] as const

const isPendingJob = (data: MutationStatusResponse | undefined): boolean =>
  data?.job?.status === 'pending' || data?.job?.status === 'in_progress'

export const useGoldenPromptsAdminListQuery = (filters: GoldenPromptFilters) => {
  return useQuery({
    queryKey: [...ADMIN_LIST_KEY, filters],
    queryFn: () => listGoldenPromptsAdmin(filters),
    keepPreviousData: true,
    retry: false,
    onError: () => {
      notification.error({ message: 'Failed to load golden prompts' })
    },
  })
}

export const useGoldenPromptAdminQuery = (promptId: string | null) => {
  return useQuery({
    queryKey: ['goldenPromptsAdmin', 'detail', promptId],
    queryFn: () => getGoldenPromptAdmin(promptId!),
    enabled: !!promptId,
    retry: false,
    onError: () => {
      notification.error({ message: 'Failed to load golden prompt' })
    },
  })
}

export const useCreateGoldenPromptAdminMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(
    (input: GoldenPromptCreateInput) => createGoldenPromptAdmin(input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(ADMIN_LIST_KEY)
        notification.success({ message: 'Golden prompt created' })
      },
      onError: () => {
        notification.error({ message: 'Failed to create golden prompt' })
      },
    },
  )
}

type UpdateVars = { promptId: string; input: GoldenPromptUpdateInput }

export const useUpdateGoldenPromptAdminMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(
    ({ promptId, input }: UpdateVars) => updateGoldenPromptAdmin(promptId, input),
    {
      onSuccess: (_data, vars) => {
        queryClient.invalidateQueries(ADMIN_LIST_KEY)
        queryClient.invalidateQueries(['goldenPromptsAdmin', 'detail', vars.promptId])
        // A text change soft-deletes existing mutants and enqueues a fresh
        // job on the backend — refetch so the status pill flips to
        // "Mutating…" without waiting for the next poll tick.
        queryClient.invalidateQueries([...MUTATION_STATUS_KEY, vars.promptId])
        notification.success({ message: 'Golden prompt updated' })
      },
      onError: () => {
        notification.error({ message: 'Failed to update golden prompt' })
      },
    },
  )
}

export const useUploadGoldenPromptsAdminMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((file: File) => uploadGoldenPromptsAdmin(file), {
    onSuccess: () => {
      queryClient.invalidateQueries(ADMIN_LIST_KEY)
    },
  })
}

export const useMutationStatusAdminQuery = (
  promptId: string | null,
  options: { enabled?: boolean } = {},
) => {
  const enabled = options.enabled !== false && !!promptId
  return useQuery({
    queryKey: [...MUTATION_STATUS_KEY, promptId],
    queryFn: () => getMutationStatusAdmin(promptId!),
    enabled,
    retry: false,
    // Worker turnaround for a typical job is seconds; poll while it's
    // active so the parent row's status pill ticks from "Mutating…" to
    // "N variants" without the user reloading.
    refetchInterval: (data) => (isPendingJob(data) ? 3000 : false),
    // No onError toast on purpose: this query runs once per visible row
    // and polls every 3s while any job is active, so a flaky endpoint
    // would flood the user. Failures are surfaced visually in the
    // status pill instead.
  })
}

export const useEnqueueMutationAdminMutation = () => {
  const queryClient = useQueryClient()
  return useMutation((promptId: string) => enqueueMutationAdmin(promptId), {
    onSuccess: (_data, promptId) => {
      queryClient.invalidateQueries([...MUTATION_STATUS_KEY, promptId])
      notification.success({ message: 'Mutation enqueued' })
    },
    onError: () => {
      notification.error({ message: 'Failed to enqueue mutation' })
    },
  })
}

export const useDeleteGoldenPromptAdminMutation = () => {
  const queryClient = useQueryClient()
  return useMutation(
    (promptId: string) => deleteGoldenPromptAdmin(promptId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(ADMIN_LIST_KEY)
        // Deleting a mutant changes its parent's variant list; deleting a
        // parent cascades to its mutants. Either way we don't know which
        // status panels are stale, so invalidate them all — they're
        // lazy-fetched per visible row, so this is cheap in practice.
        queryClient.invalidateQueries(MUTATION_STATUS_KEY)
        notification.success({ message: 'Golden prompt deleted' })
      },
      onError: () => {
        notification.error({ message: 'Failed to delete golden prompt' })
      },
    },
  )
}
